import React, { useState, useEffect } from 'react';
import { 
  Shield, CheckCircle, XCircle, AlertTriangle, BookOpen, 
  Building2, Eye, FileText, Users, Settings, ArrowRight
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../lib/api';

// Role configurations
const ROLE_CONFIGS = {
  bank_partner: {
    title: 'Welcome, Bank Partner',
    subtitle: 'Your governance and collaboration workspace',
    icon: Building2,
    iconBg: 'bg-[hsl(var(--badge-info-bg))]',
    iconColor: 'text-[hsl(var(--badge-info-text))]',
    canDo: [
      'Submit Bank Change Requests (BCRs) for new features or controls',
      'Collaborate with Granullo team via threaded comments',
      'Track progress of your requests through the lifecycle',
      'Review proposed solutions and provide feedback',
      'Sign off on completed implementations',
      'View checklist progress and test evidence'
    ],
    cannotDo: [
      'Modify live system behavior directly',
      'Approve your own requests',
      'Access other banks\' requests',
      'Edit checklists (read-only visibility)',
      'View internal Granullo discussions',
      'Execute emergency controls'
    ],
    disclaimer: 'No request you submit will directly modify live platform behavior. All accepted changes are delivered via versioned releases after formal review.',
    guides: [
      { label: 'BCR Submission Guide', path: '/guides/bank-partner' },
      { label: 'Collaboration Best Practices', path: '/guides/collaboration' }
    ]
  },
  client_admin: {
    title: 'Welcome, Client Administrator',
    subtitle: 'Your organization management workspace',
    icon: Users,
    iconBg: 'bg-[hsl(var(--badge-success-bg))]',
    iconColor: 'text-[hsl(var(--badge-success-text))]',
    canDo: [
      'Manage users within your organization',
      'Configure organization settings and preferences',
      'View and manage programs, policies, and beneficiaries',
      'Approve pending merchant and policy submissions',
      'View operational dashboard and analytics',
      'Access audit logs for your organization'
    ],
    cannotDo: [
      'Access other organizations\' data',
      'Modify platform-wide settings',
      'Execute emergency controls (Super Admin only)',
      'Create new organizations',
      'Bypass approval choreography',
      'View secrets or credentials'
    ],
    disclaimer: 'Your actions are scoped to your organization. All changes follow maker-checker approval workflows and are fully audit-logged.',
    guides: [
      { label: 'Client Admin Guide', path: '/guides/client-admin' },
      { label: 'Approval Workflows', path: '/guides/approvals' }
    ]
  },
  auditor: {
    title: 'Welcome, Auditor',
    subtitle: 'Your compliance and audit workspace',
    icon: Eye,
    iconBg: 'bg-[hsl(var(--accent-pending))]',
    iconColor: 'text-[hsl(var(--accent-pending-text))]',
    canDo: [
      'View all transactions and audit logs',
      'Access compliance reports and dashboards',
      'Review program configurations and policies',
      'Export audit data for external review',
      'View approval histories and decision trails',
      'Access beneficiary and merchant records'
    ],
    cannotDo: [
      'Modify any data or configurations',
      'Approve or reject submissions',
      'Execute any actions',
      'Access credentials or secrets',
      'View internal Granullo discussions',
      'Perform emergency actions'
    ],
    disclaimer: 'You have read-only access to audit and compliance data. No modifications can be made from your account.',
    guides: [
      { label: 'Auditor Guide', path: '/guides/auditor' },
      { label: 'Audit Log Reference', path: '/guides/audit-logs' }
    ]
  },
  regulator: {
    title: 'Welcome, Regulator',
    subtitle: 'Your regulatory oversight workspace',
    icon: Shield,
    iconBg: 'bg-[hsl(var(--badge-warning-bg))]',
    iconColor: 'text-[hsl(var(--badge-warning-text))]',
    canDo: [
      'View compliance status and reports',
      'Access program and policy documentation',
      'Review audit trails and decision histories',
      'Export regulatory reports',
      'View beneficiary and disbursement data',
      'Access real-time compliance alerts'
    ],
    cannotDo: [
      'Modify any platform data',
      'Approve or reject any submissions',
      'Execute any operational actions',
      'Access internal communications',
      'View secrets or credentials',
      'Override compliance controls'
    ],
    disclaimer: 'You have regulatory read-only access. All data is provided for oversight purposes only.',
    guides: [
      { label: 'Regulator Guide', path: '/guides/regulator' },
      { label: 'Compliance Overview', path: '/guides/compliance' }
    ]
  },
  granullo_ops: {
    title: 'Welcome, Granullo Operations',
    subtitle: 'Internal operations workspace',
    icon: Settings,
    iconBg: 'bg-[hsl(var(--badge-info-bg))]',
    iconColor: 'text-[hsl(var(--badge-info-text))]',
    canDo: [
      'Triage and assign Bank Change Requests',
      'Update BCR status through the workflow',
      'Add comments and collaborate with banks',
      'Manage checklists and request evidence',
      'Request sign-off from bank partners',
      'View operational dashboards'
    ],
    cannotDo: [
      'Execute emergency controls',
      'Mark BCRs as SHIPPED (Super Admin only)',
      'Access client credentials or secrets',
      'Bypass approval choreography',
      'Delete audit records',
      'Grant elevated permissions'
    ],
    disclaimer: 'Your internal access is fully auditable. All actions are logged with your identity and timestamp.',
    guides: [
      { label: 'BCR Operations Guide', path: '/guides/bcr-ops' },
      { label: 'Internal Policies', path: '/guides/internal' }
    ]
  },
  granullo_tech: {
    title: 'Welcome, Granullo Tech',
    subtitle: 'Internal engineering workspace',
    icon: FileText,
    iconBg: 'bg-[hsl(var(--badge-info-bg))]',
    iconColor: 'text-[hsl(var(--badge-info-text))]',
    canDo: [
      'View Bank Change Requests for technical review',
      'Add technical comments and feedback',
      'Attach test evidence and documentation',
      'Update checklist items',
      'View system status and diagnostics',
      'Access technical documentation'
    ],
    cannotDo: [
      'Triage or assign BCRs',
      'Request sign-off or ship requests',
      'Execute emergency controls',
      'Access production secrets',
      'Modify live configurations',
      'Approve client requests'
    ],
    disclaimer: 'Your technical access is read-only with limited write capabilities for documentation. All actions are audit-logged.',
    guides: [
      { label: 'Technical Review Guide', path: '/guides/tech-review' },
      { label: 'Evidence Standards', path: '/guides/evidence' }
    ]
  },
  granullo_security: {
    title: 'Welcome, Granullo Security',
    subtitle: 'Internal security and audit workspace',
    icon: Shield,
    iconBg: 'bg-[hsl(var(--badge-danger-bg))]',
    iconColor: 'text-[hsl(var(--badge-danger-text))]',
    canDo: [
      'View and audit all platform activities',
      'Review Bank Change Requests for security impact',
      'Add security-related comments',
      'Access security logs and alerts',
      'Review access patterns and anomalies',
      'Request additional security reviews'
    ],
    cannotDo: [
      'Execute emergency controls (escalate to Super Admin)',
      'Modify platform configurations',
      'Approve operational requests',
      'Access plaintext secrets',
      'Delete or modify audit records',
      'Grant permissions to other users'
    ],
    disclaimer: 'Security review access is audit-logged. Escalate any concerns to Super Admin immediately.',
    guides: [
      { label: 'Security Review Guide', path: '/guides/security-review' },
      { label: 'Incident Response', path: '/guides/incident-response' }
    ]
  }
};

export const FirstLoginModal = ({ isOpen, onAcknowledge }) => {
  const { user } = useAuth();
  const [acknowledged, setAcknowledged] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const role = user?.role;
  const config = ROLE_CONFIGS[role];
  
  if (!isOpen || !config) return null;
  
  const Icon = config.icon;

  const handleAcknowledge = async () => {
    if (!acknowledged) return;
    
    setIsSubmitting(true);
    try {
      // Log acknowledgment to audit trail
      await api.post('/auth/acknowledge-first-login', {
        acknowledged: true,
        role: role,
        timestamp: new Date().toISOString()
      });
      onAcknowledge();
    } catch (err) {
      console.error('Error logging acknowledgment:', err);
      // Still proceed even if logging fails
      onAcknowledge();
    }
    setIsSubmitting(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-card rounded-2xl max-w-2xl w-full shadow-2xl my-8">
        {/* Header */}
        <div className="p-6 border-b border-border">
          <div className="flex items-center gap-4">
            <div className={`p-3 rounded-xl ${config.iconBg}`}>
              <Icon className={`w-6 h-6 ${config.iconColor}`} />
            </div>
            <div>
              <h1 className="text-xl font-semibold text-foreground">{config.title}</h1>
              <p className="text-[13px] text-muted-foreground">{config.subtitle}</p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6 max-h-[60vh] overflow-y-auto">
          {/* What you CAN do */}
          <div>
            <h2 className="flex items-center gap-2 text-[13px] font-semibold text-foreground mb-3">
              <CheckCircle className="w-4 h-4 text-[hsl(var(--badge-success-text))]" />
              What you CAN do
            </h2>
            <ul className="space-y-2">
              {config.canDo.map((item, index) => (
                <li key={index} className="flex items-start gap-2 text-[13px] text-muted-foreground">
                  <span className="text-[hsl(var(--badge-success-text))] mt-0.5">•</span>
                  {item}
                </li>
              ))}
            </ul>
          </div>

          {/* What you CANNOT do */}
          <div>
            <h2 className="flex items-center gap-2 text-[13px] font-semibold text-foreground mb-3">
              <XCircle className="w-4 h-4 text-[hsl(var(--badge-danger-text))]" />
              What you CANNOT do
            </h2>
            <ul className="space-y-2">
              {config.cannotDo.map((item, index) => (
                <li key={index} className="flex items-start gap-2 text-[13px] text-muted-foreground">
                  <span className="text-[hsl(var(--badge-danger-text))] mt-0.5">•</span>
                  {item}
                </li>
              ))}
            </ul>
          </div>

          {/* Disclaimer */}
          <div className="bg-[hsl(var(--badge-warning-bg))] border border-[var(--g-badge-warning-border)] rounded-lg p-4">
            <div className="flex gap-3">
              <AlertTriangle className="w-5 h-5 text-[hsl(var(--badge-warning-text))] flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-[13px] font-medium text-[hsl(var(--badge-warning-text))] mb-1">Important</p>
                <p className="text-[13px] text-[hsl(var(--badge-warning-text))]">{config.disclaimer}</p>
              </div>
            </div>
          </div>

          {/* Guides */}
          {config.guides && config.guides.length > 0 && (
            <div>
              <h2 className="flex items-center gap-2 text-[13px] font-semibold text-foreground mb-3">
                <BookOpen className="w-4 h-4 text-[hsl(var(--badge-info-text))]" />
                Recommended Guides
              </h2>
              <div className="flex flex-wrap gap-2">
                {config.guides.map((guide, index) => (
                  <a
                    key={index}
                    href={guide.path}
                    className="inline-flex items-center gap-1 px-3 py-1.5 bg-secondary rounded-lg text-[13px] text-foreground hover:bg-[hsl(var(--muted))] transition-colors"
                  >
                    {guide.label}
                    <ArrowRight className="w-3 h-3" />
                  </a>
                ))}
              </div>
            </div>
          )}

          {/* How Granullo Works Link */}
          <p className="text-[12px] text-muted-foreground text-center pt-2">
            <a 
              href="/help/how-granullo-works" 
              className="text-muted-foreground hover:text-foreground underline"
            >
              See how Granullo works (step-by-step)
            </a>
          </p>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-border bg-background rounded-b-2xl">
          <label className="flex items-start gap-3 mb-4 cursor-pointer">
            <input
              type="checkbox"
              checked={acknowledged}
              onChange={(e) => setAcknowledged(e.target.checked)}
              className="mt-0.5 w-4 h-4 rounded border-border"
            />
            <span className="text-[13px] text-foreground">
              I have read and understood my role permissions and responsibilities. I acknowledge that all my actions
              are audit-logged and that this platform does not modify live system behavior through direct user actions.
            </span>
          </label>
          
          <button
            onClick={handleAcknowledge}
            disabled={!acknowledged || isSubmitting}
            className="w-full px-4 py-3 bg-primary text-primary-foreground rounded-lg text-[13px] font-medium hover:bg-foreground/80 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isSubmitting ? 'Acknowledging...' : 'I Understand, Continue'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default FirstLoginModal;
